import { useEffect, useState } from 'react';

import { Cookie, Shield, X, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LocaleLink from './LocaleLink';

const STORAGE_KEY = 'quidory.cookie-consent';
const POLICY_VERSION = '2026-02-21';

const copy = {
  ar: {
    badge: 'إشعار الخصوصية',
    h: 'نحن نقدّر خصوصيتك',
    body: 'نستخدم ملفات تعريف الارتباط الأساسية لتشغيل المنصة، وملفات اختيارية للتحليلات وتحسين تجربتك. يمكنك القبول أو الرفض أو تخصيص اختياراتك في أي وقت.',
    operatedBy: 'يُشغَّل كويدوري بواسطة Al Yakhour LLC · وايومنغ، الولايات المتحدة',
    customize: 'تخصيص',
    reject: 'رفض الاختيارية',
    accept: 'قبول الكل',
    learnMore: 'سياسة الكوكيز',
    close: 'إغلاق',
    panelH: 'إدارة تفضيلات ملفات تعريف الارتباط',
    panelSub: 'تحكَّم في كيفية استخدامنا لكل فئة. ملفات تعريف الارتباط الأساسية لا يمكن تعطيلها لأنها مطلوبة لتشغيل المنصة.',
    save: 'حفظ التفضيلات',
    saveAll: 'قبول الكل',
    categories: [
      { key: 'essential', title: 'أساسية', desc: 'مطلوبة لتسجيل الدخول والأمان والتوجيه. لا يمكن إيقافها.', locked: true },
      { key: 'functional', title: 'وظيفية', desc: 'تتذكر اللغة والوضع المظلم وتفضيلاتك في الواجهة.' },
      { key: 'analytics', title: 'تحليلية', desc: 'تساعدنا على فهم استخدام المنصة بشكل مجمَّع ومجهول الهوية.' },
      { key: 'performance', title: 'الأداء', desc: 'تقيس سرعة الصفحات والأخطاء لتحسين الأداء.' },
    ],
  },
  en: {
    badge: 'Privacy Notice',
    h: 'We value your privacy',
    body: 'We use essential cookies to operate the platform, and optional cookies for analytics and to improve your experience. You can accept, reject, or customize your choices any time.',
    operatedBy: 'Quidory is operated by Al Yakhour LLC · Wyoming, USA',
    customize: 'Customize',
    reject: 'Reject optional',
    accept: 'Accept all',
    learnMore: 'Cookie Policy',
    close: 'Close',
    panelH: 'Manage cookie preferences',
    panelSub: 'Control how each category is used. Essential cookies cannot be disabled as they are required to run the platform.',
    save: 'Save preferences',
    saveAll: 'Accept all',
    categories: [
      { key: 'essential', title: 'Essential', desc: 'Required for sign-in, security, and routing. Cannot be turned off.', locked: true },
      { key: 'functional', title: 'Functional', desc: 'Remember your language, dark mode, and other UI preferences.' },
      { key: 'analytics', title: 'Analytics', desc: 'Help us understand platform usage in aggregate, anonymously.' },
      { key: 'performance', title: 'Performance', desc: 'Measure page speed and errors so we can improve performance.' },
    ],
  },
};

const defaultPrefs = { essential: true, functional: true, analytics: false, performance: false };

const writeConsent = (status, prefs) => {
  const record = {
    status, // 'accepted' | 'rejected' | 'customized'
    prefs,
    version: POLICY_VERSION,
    entity: 'Al Yakhour LLC',
    jurisdiction: 'Wyoming, USA',
    timestamp: new Date().toISOString(),
  };
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch { /* ignore */ }
};

const CookieConsent = () => {
  const { language, isRTL } = useLanguage();
  const t = copy[language];

  const [visible, setVisible] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        // Show after a brief delay so it doesn't jump on first paint
        const id = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(id);
      }
      const parsed = JSON.parse(raw);
      if (parsed?.version !== POLICY_VERSION) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const next = { essential: true, functional: true, analytics: true, performance: true };
    writeConsent('accepted', next);
    setVisible(false);
    setShowPanel(false);
  };

  const handleRejectOptional = () => {
    const next = { essential: true, functional: false, analytics: false, performance: false };
    writeConsent('rejected', next);
    setVisible(false);
    setShowPanel(false);
  };

  const handleSavePrefs = () => {
    writeConsent('customized', { ...prefs, essential: true });
    setVisible(false);
    setShowPanel(false);
  };

  const togglePref = (key) => {
    setPrefs(p => ({ ...p, [key]: !p[key] }));
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop only when panel is open */}
      {showPanel && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowPanel(false)}
          data-testid="cookie-panel-backdrop"
        />
      )}

      {/* Banner */}
      {!showPanel && (
        <div
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-live="polite"
          aria-label={t.h}
          dir={isRTL ? 'rtl' : 'ltr'}
          className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-6 sm:end-6 sm:start-6 lg:start-auto z-50 sm:max-w-xl lg:max-w-2xl mx-auto"
        >
          <div className="relative rounded-2xl border border-gold/20 bg-card/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden">
            {/* Accent strip */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="shrink-0 p-2 rounded-lg bg-gold/10 border border-gold/20">
                  <Cookie className="h-4 w-4 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gold/80">
                    {t.badge}
                  </span>
                  <h2 className="mt-1 text-base sm:text-lg font-semibold text-foreground">
                    {t.h}
                  </h2>
                  <p className="mt-1.5 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {t.body}{' '}
                    <LocaleLink
                      to="/cookie-policy"
                      data-testid="cookie-learn-more"
                      className="text-gold hover:text-gold/80 underline-offset-2 hover:underline transition-colors"
                    >
                      {t.learnMore}
                    </LocaleLink>
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-2.5 sm:items-center sm:justify-end">
                <button
                  data-testid="cookie-customize"
                  onClick={() => setShowPanel(true)}
                  className="order-3 sm:order-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-background text-foreground text-xs font-medium hover:bg-muted/50 transition-colors"
                >
                  <Shield className="h-3.5 w-3.5" />
                  {t.customize}
                </button>
                <button
                  data-testid="cookie-reject"
                  onClick={handleRejectOptional}
                  className="order-2 inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 text-xs font-medium transition-colors"
                >
                  {t.reject}
                </button>
                <button
                  data-testid="cookie-accept"
                  onClick={handleAcceptAll}
                  className="order-1 sm:order-3 inline-flex items-center justify-center gap-1.5 px-5 py-2 rounded-lg bg-foreground text-background text-xs font-semibold hover:bg-foreground/90 transition-all hover:-translate-y-px"
                >
                  {t.accept}
                  <ChevronRight className={`h-3.5 w-3.5 ${isRTL ? 'rotate-180' : ''}`} />
                </button>
              </div>

              <p className="mt-4 text-[10px] text-muted-foreground/60 leading-relaxed">
                {t.operatedBy}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Panel */}
      {showPanel && (
        <div
          data-testid="cookie-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t.panelH}
          dir={isRTL ? 'rtl' : 'ltr'}
          className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-6 sm:end-6 sm:start-6 lg:start-auto z-[70] sm:max-w-xl lg:max-w-2xl mx-auto animate-in slide-in-from-bottom-4 fade-in duration-300"
        >
          <div className="relative rounded-2xl border border-gold/20 bg-card/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gold/80">
                    {t.badge}
                  </span>
                  <h2 className="mt-1 text-lg font-semibold text-foreground">{t.panelH}</h2>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed max-w-md">
                    {t.panelSub}
                  </p>
                </div>
                <button
                  data-testid="cookie-panel-close"
                  onClick={() => setShowPanel(false)}
                  aria-label={t.close}
                  className="shrink-0 p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-5 space-y-2 max-h-[44vh] overflow-y-auto pe-1">
                {t.categories.map((cat) => {
                  const checked = cat.locked ? true : prefs[cat.key];
                  return (
                    <div
                      key={cat.key}
                      data-testid={`cookie-cat-${cat.key}`}
                      className="flex items-start gap-3 p-3.5 rounded-xl border border-border bg-background/60"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-foreground">{cat.title}</h3>
                          {cat.locked && (
                            <span className="text-[9px] font-medium uppercase tracking-wider text-gold/80 px-1.5 py-0.5 rounded bg-gold/10 border border-gold/20">
                              {language === 'ar' ? 'دائماً' : 'Always on'}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{cat.desc}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={checked}
                        aria-label={cat.title}
                        disabled={cat.locked}
                        onClick={() => !cat.locked && togglePref(cat.key)}
                        data-testid={`cookie-toggle-${cat.key}`}
                        className={`relative shrink-0 mt-0.5 h-5 w-9 rounded-full transition-colors ${
                          checked ? 'bg-gold/80' : 'bg-muted-foreground/30'
                        } ${cat.locked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-background shadow transition-all ${
                            isRTL
                              ? (checked ? 'end-0.5' : 'end-4')
                              : (checked ? 'start-4' : 'start-0.5')
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                <LocaleLink
                  to="/cookie-policy"
                  onClick={() => setShowPanel(false)}
                  className="text-xs text-gold hover:text-gold/80 underline-offset-2 hover:underline transition-colors"
                >
                  {t.learnMore}
                </LocaleLink>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    data-testid="cookie-panel-save"
                    onClick={handleSavePrefs}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border bg-background text-foreground text-xs font-medium hover:bg-muted/50 transition-colors"
                  >
                    {t.save}
                  </button>
                  <button
                    data-testid="cookie-panel-accept-all"
                    onClick={handleAcceptAll}
                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2 rounded-lg bg-foreground text-background text-xs font-semibold hover:bg-foreground/90 transition-all hover:-translate-y-px"
                  >
                    {t.saveAll}
                    <ChevronRight className={`h-3.5 w-3.5 ${isRTL ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>

              <p className="mt-4 text-[10px] text-muted-foreground/60 leading-relaxed">
                {t.operatedBy}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
